import {
  GeckoLegacyBoilerWalls,
  type GeckoComponentModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import type { ReportHeatmapDescriptorBoilerTubeWall } from './types'

export interface BoilerTubeAxisTick {
  tick: number
  customerNumber: number
}

export function getBoilerTubeNumbering(
  componentModel: GeckoComponentModel,
  displayUnit: GeckoMeasurementUnit = GeckoMeasurementUnit.Tubes
): ReportHeatmapDescriptorBoilerTubeWall[] | undefined {
  if (!(componentModel instanceof GeckoLegacyBoilerWalls)) return undefined

  return componentModel.wallsArray.map((wall) => ({
    xStart: wall.geometry.x.min.toNumber(displayUnit),
    xEnd: wall.geometry.x.max.toNumber(displayUnit),
    customerTubeEnd: wall.customerXEnd?.toNumber(displayUnit),
    customerTubeInverted: wall.customerXInverted,
  }))
}

export function getCustomerTubeNumber(
  componentModel: GeckoComponentModel,
  x: number,
  displayUnit: GeckoMeasurementUnit
): number | undefined {
  if (!(componentModel instanceof GeckoLegacyBoilerWalls)) return undefined

  const walls = componentModel.wallsArray
  const wallIndex = walls.findIndex((wall, index) => {
    const xStart = wall.geometry.x.min.toNumber(displayUnit)
    const xEnd = wall.geometry.x.max.toNumber(displayUnit)
    return (
      x >= xStart && (x < xEnd || (index === walls.length - 1 && x <= xEnd))
    )
  })
  if (wallIndex === -1) return undefined

  const wall = walls[wallIndex]
  return wall
    .convertToCustomerTubeNumbers(
      GeckoMeasurement.CreateFromValue(x, displayUnit)
    )
    .toNumber(displayUnit)
}

export function getBoilerTubeAxisTicks(
  componentModel: GeckoComponentModel,
  xStart: number,
  xEnd: number,
  displayUnit: GeckoMeasurementUnit
): BoilerTubeAxisTick[] | undefined {
  if (!(componentModel instanceof GeckoLegacyBoilerWalls)) return undefined

  const axisStart = Math.min(xStart, xEnd)
  const axisEnd = Math.max(xStart, xEnd)
  const axisSpan = axisEnd - axisStart
  const ticks = new Map<number, number>()
  const addTick = (tick: number, customerNumber: number) => {
    const existingTick = Array.from(ticks.keys()).find(
      (candidate) => Math.abs(candidate - tick) < 1e-6
    )
    ticks.set(existingTick ?? tick, customerNumber)
  }

  const startCustomerNumber = getCustomerTubeNumber(
    componentModel,
    xStart,
    displayUnit
  )
  const endCustomerNumber = getCustomerTubeNumber(
    componentModel,
    xEnd,
    displayUnit
  )
  addTick(xStart, startCustomerNumber ?? xStart)
  addTick(xEnd, endCustomerNumber ?? xEnd)

  componentModel.wallsArray.forEach((wall) => {
    const wallStart = wall.geometry.x.min.toNumber(displayUnit)
    const wallEnd = wall.geometry.x.max.toNumber(displayUnit)
    const visibleStart = Math.max(axisStart, wallStart)
    const visibleEnd = Math.min(axisEnd, wallEnd)
    if (visibleStart >= visibleEnd || axisSpan === 0) return

    const customerStart = wall
      .convertToCustomerTubeNumbers(
        GeckoMeasurement.CreateFromValue(visibleStart, displayUnit)
      )
      .toNumber(displayUnit)
    const customerEnd = wall
      .convertToCustomerTubeNumbers(
        GeckoMeasurement.CreateFromValue(visibleEnd, displayUnit)
      )
      .toNumber(displayUnit)
    const addedCustomerNumbers = new Set<number>()
    const thirdRatios = [1 / 3, 2 / 3]
    thirdRatios.forEach((ratio) => {
      const customerNumber =
        customerStart + ratio * (customerEnd - customerStart)
      const labelNumber = Math.round(customerNumber)
      if (addedCustomerNumbers.has(labelNumber)) return
      addedCustomerNumbers.add(labelNumber)

      const tick = visibleStart + ratio * (visibleEnd - visibleStart)
      addTick(tick, customerNumber)
    })
  })

  return Array.from(ticks, ([tick, customerNumber]) => ({
    tick,
    customerNumber,
  })).sort((left, right) =>
    xEnd >= xStart ? left.tick - right.tick : right.tick - left.tick
  )
}
